/**
 * La taxonomía de errores del producto. Vive en `domain` y no en la API por una
 * razón concreta: `retryable` lo lee la outbox del cliente para decidir entre
 * reintentar y mandar la mutación a `outbox_dead` (PRD 12.3). Si la tabla
 * viviera en el servidor, el cliente tendría que duplicarla y se desincronizaría.
 *
 * Formato de salida: RFC 9457 Problem Details. `packages/contracts` tiene el
 * esquema Zod; aquí están los hechos.
 */

export const ERROR_CODES = [
  // --- petición -------------------------------------------------------------
  'invalid-request',
  'unauthenticated',
  'forbidden',
  'not-found',
  'conflict',
  'rate-limited',
  'client-too-old',
  /** Se entiende pero no se puede aplicar: fecha fuera de ventana, valor sin sentido. */
  'unprocessable',
  // --- sincronización -------------------------------------------------------
  'implausible-clock',
  'unknown-mutation-type',
  'checkpoint-too-old',
  'batch-too-large',
  // --- servidor -------------------------------------------------------------
  'internal',
  'not-ready',
  'upstream-unavailable',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

type ErrorSpec = {
  /** Estado HTTP. */
  readonly status: number;
  /** Título corto y estable, en español. Nunca lleva datos del usuario. */
  readonly title: string;
  /**
   * Si la misma petición, repetida tal cual, puede salir bien más tarde.
   * `false` es la decisión de retirar la mutación de circulación, no un matiz.
   */
  readonly retryable: boolean;
};

export const ERROR_SPECS: { readonly [K in ErrorCode]: ErrorSpec } = {
  'invalid-request': { status: 400, title: 'Petición mal formada', retryable: false },
  unauthenticated: { status: 401, title: 'Sesión no válida', retryable: false },
  forbidden: { status: 403, title: 'Sin permiso', retryable: false },
  'not-found': { status: 404, title: 'No existe', retryable: false },
  conflict: { status: 409, title: 'Conflicto de estado', retryable: false },
  'rate-limited': { status: 429, title: 'Demasiadas peticiones', retryable: true },
  'client-too-old': { status: 426, title: 'Versión de cliente no admitida', retryable: false },
  unprocessable: { status: 422, title: 'No se puede aplicar', retryable: false },

  'implausible-clock': {
    status: 422,
    title: 'Reloj del dispositivo fuera de rango',
    retryable: false,
  },
  'unknown-mutation-type': { status: 422, title: 'Tipo de mutación desconocido', retryable: false },
  'checkpoint-too-old': { status: 409, title: 'Checkpoint caducado', retryable: false },
  'batch-too-large': { status: 413, title: 'Lote demasiado grande', retryable: false },

  internal: { status: 500, title: 'Error interno', retryable: true },
  'not-ready': { status: 503, title: 'Servicio no listo', retryable: true },
  'upstream-unavailable': { status: 503, title: 'Dependencia no disponible', retryable: true },
};

/** Espacio de nombres de los `type` de Problem Details. */
export const ERROR_TYPE_BASE = 'https://selflevel.app/errors/';

export function errorTypeUri(code: ErrorCode): string {
  return `${ERROR_TYPE_BASE}${code}`;
}

export class DomainError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly retryable: boolean;
  /** Detalle para quien depura. Puede llevar datos; nunca secretos. */
  readonly detail: string | undefined;

  constructor(code: ErrorCode, detail?: string, options?: { cause?: unknown }) {
    const spec = ERROR_SPECS[code];
    super(detail ? `${spec.title}: ${detail}` : spec.title, options);
    this.name = 'DomainError';
    this.code = code;
    this.status = spec.status;
    this.retryable = spec.retryable;
    this.detail = detail;
  }
}

export function isDomainError(value: unknown): value is DomainError {
  return value instanceof DomainError;
}
